import { useState, useEffect, useCallback } from 'react';
import { getRandomizedChallenges } from '../data/challenges';

/**
 * Get rank based on percentage score
 */
function getRank(percentage) {
  if (percentage >= 90) return { rank: "SENIOR QA ARCHITECT", color: "#ffd700", score: percentage };
  if (percentage >= 70) return { rank: "QA ENGINEER", color: "#33ff33", score: percentage };
  if (percentage >= 50) return { rank: "JUNIOR TESTER", color: "#00ffff", score: percentage };
  return { rank: "INTERN", color: "#ff3333", score: percentage };
}

/**
 * Custom hook that manages all game state and logic
 * Separating game logic from UI components is a React best practice
 */
export const useGameState = () => {
  // Generate randomized challenges on initial load
  const [challenges, setChallenges] = useState(() => getRandomizedChallenges(10));
  
  const [gameState, setGameState] = useState("intro"); // intro, playing, result, complete
  const [currentLevel, setCurrentLevel] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [timerActive, setTimerActive] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [bootSequence, setBootSequence] = useState(0);

  const currentChallenge = challenges[currentLevel];

  // Timer logic
  useEffect(() => {
    if (!timerActive || timeLeft <= 0) return;
    
    const timer = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          setTimerActive(false);
          handleTimeout();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [timerActive, timeLeft]);

  const handleTimeout = useCallback(() => {
    setShowExplanation(true);
    setAnswers(prev => [...prev, { level: currentLevel, correct: false, timeout: true }]);
  }, [currentLevel]);

  const startGame = useCallback(() => {
    // Generate NEW random challenges each time the game starts
    setChallenges(getRandomizedChallenges(10));
    
    setBootSequence(1);
    setTimeout(() => setBootSequence(2), 800);
    setTimeout(() => setBootSequence(3), 1600);
    setTimeout(() => setBootSequence(4), 2400);
    setTimeout(() => {
      setGameState("playing");
      setTimerActive(true);
    }, 3200);
  }, []);

  const handleAnswer = useCallback((index) => {
    if (selectedAnswer !== null) return;
    
    setSelectedAnswer(index);
    setTimerActive(false);
    
    const isCorrect = index === currentChallenge.correct;
    if (isCorrect) {
      const timeBonus = Math.floor(timeLeft / 2);
      const difficultyMultiplier = { easy: 1, medium: 1.5, hard: 2 }[currentChallenge.difficulty];
      const points = Math.floor((100 + timeBonus) * difficultyMultiplier);
      setScore(prev => prev + points);
    }
    
    setAnswers(prev => [...prev, { level: currentLevel, correct: isCorrect }]);
    
    setTimeout(() => setShowExplanation(true), 500);
  }, [selectedAnswer, currentChallenge, timeLeft, currentLevel]);

  const nextLevel = useCallback(() => {
    if (currentLevel >= challenges.length - 1) {
      setGameState("complete");
    } else {
      setCurrentLevel(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
      setTimeLeft(60);
      setTimerActive(true);
    }
  }, [currentLevel, challenges.length]);

  const restartGame = useCallback(() => {
    setGameState("intro");
    setCurrentLevel(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setTimeLeft(60);
    setTimerActive(false);
    setAnswers([]);
    setBootSequence(0);
    // New challenges will be generated when startGame is called
  }, []);

  // Calculate final results
  const getResults = useCallback(() => {
    const correctCount = answers.filter(a => a.correct).length;
    const percentage = Math.round((correctCount / challenges.length) * 100);
    const rankInfo = getRank(percentage);
    
    return {
      score,
      correctCount,
      totalCount: challenges.length,
      percentage,
      ...rankInfo
    };
  }, [answers, challenges.length, score]);

  return {
    // State
    gameState,
    currentLevel,
    score,
    selectedAnswer,
    showExplanation,
    timeLeft,
    answers,
    bootSequence,
    currentChallenge,
    totalLevels: challenges.length,
    
    // Actions
    startGame,
    handleAnswer,
    nextLevel,
    restartGame,
    getResults
  };
};